import { clsx } from 'clsx'

import {
  Flex,
  InfoIcon,
  Skeleton,
  Text,
  TrendingUpIcon,
} from '@buffer-mono/popcorn'

import { SectionCard } from '../../../components/SectionCard'

import styles from './BlankSummaryMetrics.module.css'
import summaryStyles from '../../../components/SummaryMetrics/SummaryMetrics.module.css'

import type { JSX } from 'react'

/**
 * Representative metrics previewed to a fresh-start user. No values — the cards
 * tease the layout they'll see once a channel is connected and data flows in.
 */
export type BlankSummaryMetric = {
  label: string
  trend: 'up' | 'down'
}

const PLACEHOLDER_METRICS: BlankSummaryMetric[] = [
  { label: 'Likes', trend: 'up' },
  { label: 'Comments', trend: 'down' },
  { label: 'Impressions', trend: 'up' },
  { label: 'Engagement Rate', trend: 'down' },
]

/** Empty-state preview of the Summary metric cards. */
export function BlankSummaryMetrics({
  loading = false,
  metrics = PLACEHOLDER_METRICS,
  subtitleClassName,
}: {
  loading?: boolean
  metrics?: readonly BlankSummaryMetric[]
  subtitleClassName?: string
}): JSX.Element {
  return (
    <SectionCard>
      <SectionCard.Header>
        <SectionCard.Title>Summary</SectionCard.Title>
        <SectionCard.Subtitle className={subtitleClassName}>
          {loading ? (
            <Skeleton width={240} height={14} />
          ) : (
            <span aria-hidden>&nbsp;</span>
          )}
        </SectionCard.Subtitle>
      </SectionCard.Header>
      <ul className={summaryStyles.grid}>
        {metrics.map(({ label, trend }) => (
          <li key={label}>
            <div className={clsx(summaryStyles.metricCard, styles.metricCard)}>
              <Flex
                justify="between"
                align="center"
                fullWidth
                className={styles.labelRow}
              >
                {loading ? (
                  <Skeleton width={80} height={14} />
                ) : (
                  <Text size="sm" color="subtle">
                    {label}
                  </Text>
                )}
                {!loading && (
                  <InfoIcon color="subtle" size="xsmall" aria-hidden />
                )}
              </Flex>
              <Flex align="center" className={styles.trendRow}>
                {loading ? (
                  <Skeleton width={100} height={24} />
                ) : (
                  <TrendingUpIcon
                    color={trend === 'up' ? 'success' : 'critical'}
                    size="small"
                    className={clsx(trend === 'down' && styles.trendDown)}
                    aria-hidden
                  />
                )}
              </Flex>
              {/* Washes the card out so it reads as a preview, not live data. */}
              <Flex aria-hidden className={styles.washOverlay} />
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  )
}
